import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Building2, Phone, Trash2, UserCheck, MessageSquare, Sparkles, X, Copy, Eye, EyeOff, Wand2 } from "lucide-react";

const TAB_STYLE = (active) =>
  `px-4 py-2 border text-xs font-mono-tech uppercase tracking-widest ${
    active
      ? "border-yellow-300 text-yellow-300 bg-yellow-300/10"
      : "border-white/10 text-zinc-400 hover:border-white/30"
  }`;

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [approving, setApproving] = useState(null);
  const [approveZonas, setApproveZonas] = useState("");
  const [approvePassword, setApprovePassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [approveResult, setApproveResult] = useState(null);

  const load = () => api.get("/admin/leads?limit=500").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const filtered = leads.filter((l) => {
    if (filter === "all") return true;
    if (filter === "register") return l.type === "register";
    if (filter === "contact") return l.type === "contact";
    if (filter === "pending") return l.type === "register" && l.status !== "approved";
    return true;
  });

  const del = async (l) => {
    if (!confirm(`Eliminar ${l.type === "register" ? "solicitud de " + l.razon_social : "contacto de " + l.nombre}?`)) return;
    try {
      await api.delete(`/admin/leads/${l.id}`);
      toast.success("Lead eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approve = async () => {
    if (!approving) return;
    if (approvePassword && approvePassword.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres"); return;
    }
    try {
      const zonas = approveZonas.split(",").map((z) => z.trim()).filter(Boolean);
      const body = { zonas, photo_required_on_delivery: true };
      if (approvePassword) body.password = approvePassword;
      const r = await api.post(`/admin/leads/${approving.id}/approve`, body);
      setApproveResult({ ...r.data, email: approving.email, razon_social: approving.razon_social });
      setApproving(null); setApproveZonas(""); setApprovePassword(""); setShowPass(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const genPass = () => {
    const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let p = "";
    for (let i = 0; i < 10; i++) p += chars[Math.floor(Math.random() * chars.length)];
    setApprovePassword(p); setShowPass(true);
  };

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-2">/ Leads</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Solicitudes públicas</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-xl">Contactos y solicitudes de alta recibidos desde la landing pública.</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <button data-testid="lead-tab-all" onClick={() => setFilter("all")} className={TAB_STYLE(filter === "all")}>Todos ({leads.length})</button>
        <button data-testid="lead-tab-pending" onClick={() => setFilter("pending")} className={TAB_STYLE(filter === "pending")}>
          Pendientes de alta ({leads.filter((l) => l.type === "register" && l.status !== "approved").length})
        </button>
        <button data-testid="lead-tab-register" onClick={() => setFilter("register")} className={TAB_STYLE(filter === "register")}>
          Registro aliado ({leads.filter((l) => l.type === "register").length})
        </button>
        <button data-testid="lead-tab-contact" onClick={() => setFilter("contact")} className={TAB_STYLE(filter === "contact")}>
          Contacto ({leads.filter((l) => l.type === "contact").length})
        </button>
      </div>

      <div className="space-y-3">
        {filtered.map((l) => (
          <div key={l.id} className="border border-white/10 bg-zinc-900/50 hover:border-white/20 p-5" data-testid={`lead-${l.id}`}>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] font-mono-tech uppercase tracking-widest px-2 py-0.5 border ${l.type === "register" ? "border-yellow-300/40 text-yellow-300" : "border-zinc-500/40 text-zinc-400"}`}>
                    {l.type === "register" ? "Alta aliado" : "Contacto"}
                  </span>
                  {l.status === "approved" && (
                    <span className="text-[10px] font-mono-tech uppercase tracking-widest px-2 py-0.5 border border-emerald-500/40 text-emerald-400">Aprobado</span>
                  )}
                  <span className="text-[10px] text-zinc-500 font-mono-tech">{(l.created_at || "").slice(0, 19).replace("T", " ")}</span>
                </div>
                <div className="font-display font-black text-xl tracking-tighter truncate">
                  {l.type === "register" ? l.razon_social : l.nombre}
                </div>
                <div className="flex items-center gap-4 mt-2 text-xs text-zinc-400 flex-wrap">
                  {l.type === "register" && <><span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> Contacto: {l.nombre_contacto}</span></>}
                  <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {l.email}</span>
                  {(l.telefono) && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {l.telefono}</span>}
                  {l.type === "register" && l.comuna_bodega && <span>· Bodega: <b className="text-white">{l.comuna_bodega}</b></span>}
                  {l.type === "register" && l.volumen_mensual && <span>· Vol: <b className="text-white">{l.volumen_mensual}</b></span>}
                </div>
                {l.type === "contact" && l.mensaje && (
                  <div className="mt-3 border-l-2 border-yellow-300/40 pl-3 text-sm text-zinc-300 italic bg-black/30 py-2 pr-2 flex gap-2 items-start">
                    <MessageSquare className="w-3.5 h-3.5 text-zinc-500 mt-1 flex-shrink-0" />
                    <span>{l.mensaje}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {l.type === "register" && l.status !== "approved" && (
                  <button onClick={() => setApproving(l)} className="border border-yellow-300/50 text-yellow-300 hover:bg-yellow-300/10 font-bold px-3 py-2 uppercase tracking-widest text-xs inline-flex items-center gap-1.5" data-testid={`approve-${l.id}`}>
                    <UserCheck className="w-3.5 h-3.5" /> Aprobar
                  </button>
                )}
                <button onClick={() => del(l)} className="w-8 h-8 border border-white/10 hover:border-red-500/50 hover:text-red-400 inline-flex items-center justify-center" title="Eliminar" data-testid={`delete-lead-${l.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="border border-white/10 border-dashed p-10 text-center text-zinc-500 font-mono-tech text-sm">
            Sin leads en esta vista
          </div>
        )}
      </div>

      {/* Approve modal */}
      {approving && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setApproving(null)}>
          <div className="bg-zinc-950 border border-yellow-300/40 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest">/ Aprobar aliado</div>
                <div className="font-display font-black text-2xl tracking-tighter mt-1 truncate">{approving.razon_social}</div>
              </div>
              <button onClick={() => setApproving(null)} className="text-zinc-400"><X className="w-5 h-5" /></button>
            </div>

            {/* Resumen del lead */}
            <div className="border border-white/10 bg-black/40 p-3 mb-5 text-xs space-y-1 font-mono-tech">
              <div><span className="text-zinc-500">Contacto:</span> <span className="text-white">{approving.nombre_contacto}</span></div>
              <div><span className="text-zinc-500">Email:</span> <span className="text-yellow-300">{approving.email}</span></div>
              <div><span className="text-zinc-500">Tel:</span> <span className="text-white">{approving.telefono || "-"}</span></div>
              {approving.comuna_bodega && <div><span className="text-zinc-500">Bodega:</span> <span className="text-white">{approving.comuna_bodega}</span></div>}
              {approving.volumen_mensual && <div><span className="text-zinc-500">Volumen:</span> <span className="text-white">{approving.volumen_mensual}</span></div>}
            </div>

            <label className="block text-xs font-mono-tech uppercase tracking-widest text-zinc-500 mb-1">Zonas de operación (opcional)</label>
            <input value={approveZonas} onChange={(e) => setApproveZonas(e.target.value)} className="w-full bg-zinc-900 border border-white/20 focus:border-yellow-300 outline-none px-3 py-2 text-sm mb-4" placeholder="Providencia, Ñuñoa" data-testid="approve-zonas" />

            <label className="block text-xs font-mono-tech uppercase tracking-widest text-zinc-500 mb-1">Contraseña del cliente</label>
            <div className="flex items-center bg-zinc-900 border border-white/20 focus-within:border-yellow-300 mb-1">
              <input
                type={showPass ? "text" : "password"}
                value={approvePassword}
                onChange={(e) => setApprovePassword(e.target.value)}
                className="flex-1 bg-transparent outline-none px-3 py-2 text-sm font-mono-tech"
                placeholder="Mínimo 6 caracteres — vacío = auto"
                data-testid="approve-password"
              />
              <button type="button" onClick={() => setShowPass(!showPass)} className="px-3 text-zinc-400 hover:text-white" title={showPass ? "Ocultar" : "Mostrar"}>
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button type="button" onClick={genPass} className="px-3 border-l border-white/10 text-zinc-400 hover:text-yellow-300" title="Generar contraseña" data-testid="approve-gen-pass">
                <Wand2 className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-zinc-500 mb-5">Se enviará al cliente por email junto con el mensaje de bienvenida.</p>

            <button onClick={approve} className="w-full bg-yellow-300 text-black font-bold p-3 uppercase tracking-widest text-sm hover:bg-yellow-200" data-testid="confirm-approve">
              Crear cliente y enviar credenciales
            </button>
          </div>
        </div>
      )}

      {/* Approve result */}
      {approveResult && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setApproveResult(null)}>
          <div className="bg-zinc-950 border border-emerald-500/40 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-full bg-emerald-500 mx-auto mb-4 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-black" />
            </div>
            <div className="text-center mb-5">
              <div className="font-display font-black text-2xl tracking-tighter">Aliado creado</div>
              <div className="text-sm text-zinc-400 mt-1">{approveResult.razon_social}</div>
            </div>
            <div className="border border-white/10 p-3 mb-4 font-mono-tech text-sm space-y-1">
              <div><span className="text-zinc-500">Email:</span> <span className="text-white">{approveResult.email}</span></div>
              <div><span className="text-zinc-500">Pass temporal:</span> <span className="text-yellow-300">{approveResult.temp_password}</span></div>
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(`Email: ${approveResult.email}\nPass: ${approveResult.temp_password}`);
                toast.success("Credenciales copiadas");
              }}
              className="w-full border border-white/20 hover:border-yellow-300 hover:text-yellow-300 font-bold p-3 uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 mb-2"
              data-testid="copy-creds"
            >
              <Copy className="w-3.5 h-3.5" /> Copiar credenciales
            </button>
            <button onClick={() => setApproveResult(null)} className="w-full bg-yellow-300 text-black font-bold p-3 uppercase tracking-widest text-sm hover:bg-yellow-200">
              Listo
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}
